import { Fancybox, Swiper, A11y, Thumbs, Navigation } from 'global-modules';

class ProductMediaSlider extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.fancybox();

    // No slider markup or nothing to slide: skip Swiper entirely
    if (!this.querySelector('[data-product-media-swiper]')) return;
    if (this.querySelectorAll('.swiper-slide').length <= 1) return;

    this.thumbnailSwiper();
    this.mainSwiper();

    // Slide to variant from URL on initial load
    const urlVariantId = parseInt(new URLSearchParams(window.location.search).get('variant'));
    if (urlVariantId) this.slideToVariant(urlVariantId);

    // Listen for variant change and go to slide
    document.addEventListener('product:variant-change', event => this.slideToVariant(event.detail.variantId));

    // Pastes: the first slide is the pack combination, go back to it when a flavour or the pack size changes (paste-pdp.js)
    document.addEventListener('paste:selection-change', () => this.goToSlide(0));

    this.listeners = {
      resize: debounce(() => {
        if (!this.swiperThumbsInstance) return;
        const isHorizontal = !screen.orientation.type.includes('landscape') || window.innerWidth < 1024;
        this.swiperThumbsInstance.changeDirection(isHorizontal ? 'horizontal' : 'vertical', true);
      }, 150),
    };

    window.addEventListener('resize', this.listeners.resize);
  }

  disconnectedCallback() {
    if (this.listeners) window.removeEventListener('resize', this.listeners.resize);
  }

  /*
    * Create thumb slider
    */
  thumbnailSwiper() {
    // Check if Swiper exists
    if (!Swiper) return false;

    // Get Thumbnails slider element & Set swiper options
    this.swiperThumbs = this.querySelector('[data-product-media-thumbnails-slider]');
    if (!this.swiperThumbs) return false;

    this.swiperThumbsOptions = {
      a11y: true,
      threshold: 10,
      observer: true,
      spaceBetween: 8,
      direction: 'horizontal',
      slidesPerView: 'auto',
      modules: [A11y, Thumbs],
      on: {
        init: (function(swiper) {
          const isHorizontal = !screen.orientation.type.includes('landscape') || window.innerWidth < 1024;
          swiper.changeDirection(isHorizontal ? 'horizontal' : 'vertical', true);
          setTimeout(() => {
            swiper.el.classList.remove('opacity-0');
          }, 300);
        }),
      },
    };

    // Create Thumbnails slider
    this.swiperThumbsInstance = new Swiper(this.swiperThumbs, this.swiperThumbsOptions);

    // Rebuild Swiper in design mode after delay
    if (Shopify.designMode && this.swiperThumbsInstance) {
      window.addEventListener('shopify:section:load', () => {
        setTimeout(() => {
          this.swiperThumbsInstance.update();
        }, 300);
      });
    }
  }

  /*
    * Create main slider
    */
  mainSwiper() {
    // Check if Swiper exists
    if (!Swiper) return false;

    // Get swiper element
    this.swiperMain = this.querySelector('[data-product-media-swiper]');

    // Get thumb options
    let thumbOptions;
    if (this.swiperThumbsInstance) {
      thumbOptions = {
        thumbs: {
          swiper: this.swiperThumbsInstance,
          slideThumbActiveClass: 'swiper-slide-thumb-active',
        },
      };
    }

    // Get initial slide and swiper index
    let initialSlideIndex = 0;
    const initialVariantId = parseInt(this.swiperMain.dataset.productMediaSlider);

    if (initialVariantId) {
      const initialSlide = this.matchVariantIdWithSlide(initialVariantId);
      initialSlideIndex = initialSlide ? initialSlide : initialSlideIndex;
    }

    // Swiper options
    this.swiperMainOptions = {
      a11y: true,
      rewind: true,
      observer: true,
      initialSlide: initialSlideIndex,
      modules: [A11y, Thumbs, Navigation],
      navigation: {
        nextEl: this.swiperMain.querySelector('.swiper-button-next'),
        prevEl: this.swiperMain.querySelector('.swiper-button-prev'),
      },
      ...thumbOptions,
      on: {
        beforeSlideChangeStart: (function(swiper) {
          const currentSlide = swiper.slides[swiper.activeIndex];
          const currentSlideType = currentSlide.dataset.slideType;
          if (currentSlideType === 'video') {
            const video = currentSlide.querySelector('video');
            if (video) {
              // pause currently playing video when slide is changed
              video.pause();
            }
          }
        }),
        slideChange: (function(swiper) {
          const currentSlide = swiper.slides[swiper.activeIndex];
          const currentSlideType = currentSlide.dataset.slideType;
          if (currentSlideType === 'video') {
            const video = currentSlide.querySelector('video');
            if (video) {
              // play video when new slide has video
              video.play();
            }
          }
        }),
      },
    };

    // Create Main slider
    this.swiperMainInstance = new Swiper(this.swiperMain, this.swiperMainOptions);

    // Rebuild Swiper in design mode
    if (Shopify.designMode && this.swiperMainInstance) {
      window.addEventListener('shopify:section:load', () => {
        this.swiperMainInstance.update();
      });
    }
  }

  /*
    * Create fancybox
    */
  fancybox() {
    // Check if Fancybox exists
    if (!Fancybox) return false;

    Fancybox.bind('[data-fancybox]', {
      dragToClose: false,
      showClass: 'f-fadeIn',
      hideClass: 'f-fadeOut',
      mainClass: 'fancybox-products',
      Carousel: {
        transition: 'slide',
      },
      // Disable image zoom animation on opening and closing
      Images: {
        zoom: false,
      },
      Thumbs: {
        type: 'classic',
      },
      Toolbar: {
        display: {
          left: [],
          middle: [],
          right: ['close'],
        },
      },
      on: {
        done: fancybox => {
          const carousel = fancybox.carousel;
          fancybox.userSlides.forEach((element, index) => {
            const altText = element.triggerEl.querySelector('img')?.getAttribute('alt');
            if (carousel.slides[index].imageEl) {
              carousel.slides[index].imageEl.alt = altText ? altText : carousel.slides[index].imageEl.alt;
            }
          });
        },
      },
    });
  }

  /*
    * Match variant id with slide containing image
    * @param id {int}: variant id
    */
  matchVariantIdWithSlideImage(id) {
    if (!id || !this.swiperMainInstance) return false;

    // Get all slides
    const slides = this.swiperMainInstance.slides;

    // Find the first slide that contains an image with matching variant id
    const slide = [...slides].find(slide => {
      // Find image with data-product-variant-image attribute within the slide
      const variantImageIds = slide.dataset.productVariantImage;

      if (!variantImageIds) return false;

      // Get variant id's attached to image
      let slideVariants = JSON.parse(variantImageIds);
      return slideVariants.includes(id);
    });

    // return false if no slide found
    if (!slide) return false;

    // return matched slide index
    return this.getNodeIndex(slide);
  }

  /*
    * Slide to variant
    * @Param event {object}: event object with variant
    */
  slideToVariant(variantId) {
    if (variantId) {
      // Try new method first, fall back to old method if no match found
      const slideIndex = this.matchVariantIdWithSlideImage(variantId) ?? this.matchVariantIdWithSlide(variantId);

      if (typeof slideIndex == 'number') this.goToSlide(slideIndex);
    }
  }

  /*
    * Match variant id with slide
    * @param id {int}: variant id
    */
  matchVariantIdWithSlide(id) {
    if (!id || !this.swiperMainInstance) return false;

    // Get slides
    const slides = this.swiperMain.querySelectorAll('[data-product-variant-image]');

    // Match id with slides id's
    const slide = [...slides].find(slide => {
      // Get variant id's attached to slide
      const slideVariants = JSON.parse(slide.dataset.productVariantImage);

      // Check if variant id match the slide variant id's
      if (slideVariants.includes(id)) return slide;
    });

    // return false if no slide found
    if (!slide) return false;

    // return matched slide index
    return this.getNodeIndex(slide);
  }

  /*
    * go to slide
    * @param slide {node} / {int}: slide node / index
    */
  goToSlide(slide) {
    if ((typeof slide != 'number' && typeof slide != 'object') || !this.swiperMainInstance) return false;

    // Get index, transform node to slide index or string to int
    const index = typeof slide == 'number' ? parseInt(slide) : this.getNodeIndex(slide);

    // go to slide
    this.swiperMainInstance.slideTo(index);
  }

  getNodeIndex(node) {
    return Array.from(node.parentNode.children).indexOf(node);
  }
}

if (!customElements.get('product-media-swiper')) {
  customElements.define('product-media-swiper', ProductMediaSlider);
}